import { PureComponent } from "react";
import PropTypes from "prop-types";

import { segmentTrack } from "util/segment";
import { EVENT } from "constants/analytics";

import "./google_lookup_wrapper.scss";

// You can see the address component documentation here:
// https://developers.google.com/maps/documentation/geocoding/start#Types
const findAddressComponentValue = (type, address_components, use_long) => {
  const correctComponent = address_components.find((addressComponent) => {
    return addressComponent.types.includes(type);
  });
  return correctComponent?.[use_long ? "long_name" : "short_name"];
};

const convertGoogleAddress = (place) => {
  const addressComponents = place.address_components;
  const streetNumber = findAddressComponentValue("street_number", addressComponents);
  const streetName = findAddressComponentValue("route", addressComponents, true);
  const line1 = streetNumber ? [streetNumber, streetName].join(" ") : streetName;
  const city = findAddressComponentValue("locality", addressComponents);
  const state = findAddressComponentValue("administrative_area_level_1", addressComponents);
  const country = findAddressComponentValue("country", addressComponents);
  const postal = findAddressComponentValue("postal_code", addressComponents);
  return {
    line1,
    city,
    state,
    country,
    postal,
  };
};

class GoogleLookupWrapper extends PureComponent {
  autocomplete;
  charactersTyped;
  inputRef;
  autocompleteListener;
  line1;

  componentWillUnmount() {
    if (window.google) {
      // https://stackoverflow.com/questions/33049322/no-way-to-remove-google-places-autocomplete
      window.google.maps.event.removeListener(this.autocompleteListener);
      window.google.maps.event.clearInstanceListeners(this.inputRef);
    }
  }

  handleAddressSelect = () => {
    const place = this.autocomplete.getPlace();
    // this is called when someone presses enter on the input without selecting an address so ignore it if that happens
    if (place?.address_components) {
      const convertedPlace = convertGoogleAddress(place);
      this.line1 = convertedPlace.line1; // We save the line1 to use and override Google's attempt to set the ref
      segmentTrack(EVENT.ADDRESS_AUTOCOMPLETE_GOOGLE, {
        charactersTyped: this.charactersTyped,
        addressLength: place.formatted_address.length,
      });
      this.props.onAddressSelect(convertedPlace);
    }
  };

  // This is kinda hacky. When the place_changed fires, it changes the value of the input, but doesn't trigger the
  // onChange. We're interested in how many characters the user types in before they choose an address to see
  // the amount of time/typing saved.
  // This is also important because we might want to update the radius that we search for address around if we determine
  // that the results coming up are not relevant to the user
  handleInputChange = (event) => {
    this.charactersTyped = event.target.value.length;
  };

  initializeLookup = (inputRef, options) => {
    if (window.google && this.inputRef !== inputRef) {
      const { autoComplete } = this.props;
      this.inputRef = inputRef;

      // https://stackoverflow.com/a/49161445
      // This is to disable Google Chrome's autocomplete:
      // As of Google Chrome 72, Google Maps API is changing the autocomplete attribute to "off".
      // However, Google Chrome ignores autocomplete="off" as an attribute and still tries to autocomplete it
      // In order to get past this, we intercept anytime our input's autocomplete attribute gets changed and set
      // it back to what we want it to be. In this case, we use a random string so Google Chrome doesn't know
      // what to fill it with.
      // Note: This should only be done IF we want to disable autocomplete on the google lookup
      if (!autoComplete) {
        const autoCompleteInterceptor = new MutationObserver(() => {
          const randomString = "random-string-to-not-autocomplete";
          if (this.inputRef.getAttribute("autocomplete") !== randomString) {
            // Use random string so Google Chrome doesn't autofill
            this.inputRef.setAttribute("autocomplete", randomString);
          }
        });
        autoCompleteInterceptor.observe(this.inputRef, {
          attributes: true,
          attributeFilter: ["autocomplete"],
        });
      }

      this.autocomplete = new window.google.maps.places.Autocomplete(inputRef, {
        types: ["address"],
        ...options,
      });
      this.autocomplete.setFields(["address_components", "formatted_address"]);
      this.autocompleteListener = this.autocomplete.addListener(
        "place_changed",
        this.handleAddressSelect,
      );
      this.inputRef.addEventListener("keydown", (e) => {
        // There is an issue where the enter to select an element in the list is being propagated into the form
        // and the form saves while populating the address. This is causes problems because it looks like the address is
        // persisted. This is a bit of a hack, but if there is an item selected, then we know we don't want to propagate
        // the enter key press to the form to submit it
        // https://developers.google.com/maps/documentation/javascript/places-autocomplete#style_autocomplete
        if (e.key === "Enter" && document.getElementsByClassName("pac-item-selected").length > 0) {
          e.preventDefault();
        }
      });

      if (!this.props.allowChangeInput) {
        // The following code is used so that google doesn't try to update the value of the input when we click on an entry
        // in the autocomplete menu or when we arrow down into the autocomplete menu.
        // Google sets the value directly, so we stop that here.
        // Original code is here:
        // https://stackoverflow.com/questions/42427606/event-when-input-value-is-changed-by-javascript/48528450#48528450
        // However, we only want to scope it to this particular input.
        const descriptor = Object.getOwnPropertyDescriptor(this.inputRef, "value");
        const originalSet = descriptor.set;

        // define our own setter
        descriptor.set = (...args) => {
          // More inspiration for this code:
          // https://stackoverflow.com/questions/25692666/how-to-stop-address-from-populating-autocomplete-with-google-places
          if (this.inputRef.getAttribute("value") !== args[0]) {
            if (this.line1) {
              originalSet.apply(this.inputRef, [this.line1]); // The apply takes in an object/array
              this.line1 = "";
            }
          } else {
            originalSet.apply(this.inputRef, args);
          }
        };

        Object.defineProperty(this.inputRef, "value", descriptor);
      }
    }
  };

  render() {
    const { children } = this.props;
    return children(this.initializeLookup, this.handleInputChange);
  }
}

GoogleLookupWrapper.propTypes = {
  autoComplete: PropTypes.bool,
  onAddressSelect: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
  allowChangeInput: PropTypes.bool,
};

GoogleLookupWrapper.defaultProps = {
  autoComplete: true,
  disabled: false,
};

export default GoogleLookupWrapper;
